import {
  IStockTake,
  IStockTakeCreate,
  StockTakeStatus,
  STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
} from '../models';
import {
  clampBaseItemLimit,
  stockTakeMutualExclusivePopulationMessage,
  STOCK_TAKE_BASE_ITEM_LIMIT,
} from './stock-take-limits.utils';
import { hasStockTakeFilterGroups } from './stock-take-filter.utils';

/**
 * Validate stock take creation data
 */
export function validateStockTakeCreate(
  data: IStockTakeCreate,
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  if (!data.name || data.name.trim().length === 0) {
    errors.push('Name is required');
  }

  if (data.name && data.name.length > 100) {
    errors.push('Name must be less than 100 characters');
  }

  const hasFilter = hasStockTakeFilterGroups(data);
  const hasUncounted = !!data.countUncountedDays;
  if (hasFilter && hasUncounted) {
    errors.push(stockTakeMutualExclusivePopulationMessage());
  }

  if (hasUncounted && data.uncountedItemLimit !== undefined) {
    const clamped = clampBaseItemLimit(data.uncountedItemLimit);
    if (clamped !== data.uncountedItemLimit) {
      errors.push(`Max items to add cannot exceed ${STOCK_TAKE_BASE_ITEM_LIMIT}.`);
    }
  }

  if (hasFilter && data.itemLimit !== undefined) {
    const clamped = clampBaseItemLimit(data.itemLimit);
    if (clamped !== data.itemLimit) {
      errors.push(`Max items to add cannot exceed ${STOCK_TAKE_BASE_ITEM_LIMIT}.`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/**
 * Check if a stock take can be edited
 */
export function canEditStockTake(status: StockTakeStatus | typeof STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS): boolean {
  return (
    status === StockTakeStatus.inProgress ||
    status === STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS ||
    status === StockTakeStatus.prepared
  );
}

/**
 * Check if a stock take can be deleted
 */
export function canDeleteStockTake(
  status: StockTakeStatus | typeof STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS,
): boolean {
  return (
    status !== StockTakeStatus.submitted &&
    status !== StockTakeStatus.success &&
    status !== StockTakeStatus.deleting
  );
}

/**
 * Get Ionic badge colour token for a stock take status
 */
export function getStatusBadgeColor(status: string): string {
  switch (status) {
    case StockTakeStatus.inProgress:
    case STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS:
      return 'primary';
    case StockTakeStatus.prepared:
      return 'tertiary';
    case StockTakeStatus.submitted:
      return 'secondary';
    case StockTakeStatus.success:
      return 'success';
    case StockTakeStatus.failed:
    case StockTakeStatus.deleting:
      return 'danger';
    default:
      return 'medium';
  }
}

/**
 * Stock takes shown on the mobile list (active counting — not terminal outcomes).
 */
export function isMobileStockTakeListVisible(adjustment: IStockTake): boolean {
  return (
    adjustment.status !== StockTakeStatus.submitted &&
    adjustment.status !== StockTakeStatus.success &&
    adjustment.status !== StockTakeStatus.failed
  );
}

export function formatStatus(status: string): string {
  switch (status) {
    case StockTakeStatus.inProgress:
    case STOCK_TAKE_LEGACY_PENDING_DOCUMENT_STATUS:
      return 'In Progress';
    case StockTakeStatus.prepared:
      return 'Prepared';
    case StockTakeStatus.submitted:
      return 'Submitted';
    case StockTakeStatus.success:
      return 'Completed';
    case StockTakeStatus.failed:
      return 'Failed';
    case StockTakeStatus.deleting:
      return 'Deleting';
    default:
      return status;
  }
}
